import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

import { useIncidentCategoryBuckets } from '../../services/api/hooks/useIncidentCategoryBuckets';
import { useFilterStore } from '../filters/filterStore';
import { ALL_CATEGORIES, CATEGORY_SHORT_LABELS } from '../incidents/incidentLabels';

const COLORS = ['#18181b', '#52525b', '#a1a1aa', '#d4d4d8', '#71717a', '#3f3f46'];

export const IncidentDonut = () => {
  const { entityIds, contractIds, siteIds, dateRange } = useFilterStore();
  const query = useIncidentCategoryBuckets({ entityIds, contractIds, siteIds, dateRange });

  const buckets = (query.data?.buckets ?? []).filter(b => ALL_CATEGORIES.includes(b.category));
  const total = buckets.reduce((sum, b) => sum + b.count, 0);
  const data = buckets.map(b => ({
    name: CATEGORY_SHORT_LABELS[b.category],
    value: b.count,
  }));

  return (
    <article className="rounded-md border border-zinc-200 bg-white p-6 flex flex-col gap-5">
      <header className="flex items-baseline justify-between">
        <div>
          <h2 className="text-sm font-medium text-zinc-900">Répartition des incidents</h2>
          <p className="text-xs text-zinc-500 mt-0.5">Par catégorie, période en cours</p>
        </div>
        {!query.isLoading && total > 0 ? (
          <p className="text-xs font-mono tabular-nums text-zinc-500">
            {total} <span className="text-zinc-400">au total</span>
          </p>
        ) : null}
      </header>

      {query.isLoading ? (
        <div className="h-[280px] flex items-center justify-center">
          <div className="h-32 w-32 rounded-full bg-zinc-100 skeleton-pulse" />
        </div>
      ) : data.length === 0 ? (
        <div className="h-[280px] flex flex-col items-center justify-center gap-2 text-center">
          <p className="text-sm text-zinc-900">Aucun incident sur la période.</p>
          <p className="text-xs text-zinc-500">Ajustez la période pour afficher davantage de résultats.</p>
        </div>
      ) : (
        <div className="flex flex-row items-center gap-8">
          <div className="h-[200px] w-[200px] shrink-0 relative">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={68}
                  outerRadius={96}
                  paddingAngle={1}
                  stroke="none"
                >
                  {data.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    background: '#ffffff',
                    border: '1px solid #e4e4e7',
                    borderRadius: 4,
                    fontSize: 12,
                    padding: '6px 10px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                  }}
                  cursor={{ fill: 'transparent' }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-xs uppercase tracking-wide text-zinc-500">Total</span>
              <span className="font-mono tabular-nums text-2xl font-medium text-zinc-900 mt-0.5">{total}</span>
            </div>
          </div>

          <ul className="flex-1 flex flex-col gap-2.5">
            {data.map((d, i) => {
              const pct = total > 0 ? (d.value / total) * 100 : 0;
              return (
                <li key={d.name} className="flex items-center justify-between gap-3 text-sm">
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="h-2 w-2 rounded-full shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                    <span className="text-zinc-700 truncate">{d.name}</span>
                  </span>
                  <span className="flex items-center gap-2 shrink-0 font-mono tabular-nums">
                    <span className="text-zinc-900">{d.value}</span>
                    <span className="text-zinc-400 text-xs w-10 text-right">{pct.toFixed(0)} %</span>
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </article>
  );
};
